import { Reveal, SectionHead } from "./Reveal";
import { Check, Zap } from "lucide-react";
import { scrollTo } from "@/lib/site";

const PLANS = [
  {
    name: "Starter", price: "₹14,999*", tag: "For individuals and small businesses starting online.",
    cta: "Get Started", fast: true,
    features: ["Professional website", "Up to 5 pages", "Mobile responsive", "Modern premium design", "Contact form", "WhatsApp integration", "Google Maps integration", "Basic SEO setup", "Social media links", "Basic performance optimization"],
  },
  {
    name: "Business", price: "₹24,999*", tag: "For established businesses that need a stronger online presence.",
    cta: "Choose Business", popular: true, fast: true,
    features: ["Everything in Starter", "Up to 10 pages", "Custom UI design", "Advanced animations", "Premium sections", "WhatsApp enquiry system", "Gallery / portfolio", "SEO-ready structure", "Speed optimization", "Lead-generation sections"],
  },
  {
    name: "Professional", price: "₹39,999*", tag: "For businesses looking for a premium digital presence.",
    cta: "Go Professional",
    features: ["Everything in Business", "Custom website design", "Advanced UI/UX", "Premium animations", "Advanced enquiry system", "Multiple business sections", "Advanced SEO setup", "Conversion-focused design", "Performance optimization", "Priority support"],
  },
  {
    name: "Custom", price: "Custom Quote", tag: "For businesses requiring specialized functionality.",
    cta: "Request Custom Quote",
    features: ["Custom design", "Custom functionality", "E-commerce", "Advanced integrations", "Custom dashboards where required", "Third-party integrations", "Custom features", "Scalable architecture"],
  },
];

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="relative bg-ink py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <SectionHead
        center
        eyebrow="Pricing"
        title="Premium websites. Without the premium agency price."
        sub="Professional website solutions designed to deliver more value for your investment."
      />
      <Reveal delay={0.2}>
        <div className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-2xl border border-accent/20 bg-accent/5 px-6 py-4 text-center" data-testid="pricing-value-banner">
          <span className="font-display text-xs font-extrabold uppercase tracking-[0.2em] text-accent">Why pay traditional agency prices?</span>
          <span className="text-xs text-zinc-400">Premium Quality + Professional Design + Affordable Pricing + Fast Launch</span>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {PLANS.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.08} className="h-full">
            <div
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`relative flex h-full flex-col rounded-2xl border p-7 transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 ${
                p.popular
                  ? "border-accent/50 bg-gradient-to-b from-accent/[0.08] to-charcoal shadow-[0_0_50px_rgba(0,229,255,0.12)]"
                  : "border-white/10 bg-charcoal hover:border-white/20"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-4 py-1 font-display text-[10px] font-black uppercase tracking-[0.2em] text-ink" data-testid="pricing-popular-badge">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-zinc-300">{p.name}</h3>
              <p className="mt-4 font-display text-3xl font-black tracking-tight text-white">{p.price}</p>
              <p className="mt-2 text-xs leading-relaxed text-zinc-500">{p.tag}</p>
              {p.fast && (
                <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-display text-[9px] font-bold uppercase tracking-[0.15em] text-accent">
                  <Zap className="h-3 w-3" /> 24-Hour Launch Available
                </span>
              )}
              <ul className="mt-6 flex-1 space-y-2.5 border-t border-white/10 pt-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[13px] text-zinc-400">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollTo("#enquiry")}
                data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`mt-7 w-full rounded-full py-3.5 font-display text-[11px] font-bold uppercase tracking-[0.15em] transition-[background-color,color,box-shadow] duration-300 ${
                  p.popular
                    ? "bg-accent text-ink hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.4)]"
                    : "border border-white/20 text-white hover:border-accent/60 hover:bg-white/5"
                }`}
              >
                {p.cta}
              </button>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <p className="mt-10 text-center text-xs text-zinc-600" data-testid="pricing-disclaimer">
          *Starting prices. Final pricing depends on project requirements, content, functionality, integrations and scope.
        </p>
      </Reveal>
    </div>
  </section>
);
